import { useEffect, useRef, useState } from "react";
import { useGetIdentity } from "ra-core";
import { getSupabaseClient } from "@/components/atomic-crm/providers/supabase/supabase";
import { Link } from "react-router";
import { ArrowDown, ArrowUp } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { SERA_PHONE } from "@/components/atomic-crm/misc/constants";

const supabase = getSupabaseClient();

type ConversationRow = {
  id: string;
  caller_name: string | null;
  phone_number: string | null;
  appointment_booked: boolean | null;
  duration_seconds: number | null;
  summary: string | null;
  appointment_date: string | null;
  appointment_time: string | null;
  created_at: string;
};

type AppointmentRow = {
  id: string;
  patient_name: string | null;
  caller_name: string | null;
  appointment_date: string | null;
  appointment_time: string | null;
  reason_for_visit: string | null;
  status: string | null;
};

const fmt = (seconds: number | null) => {
  if (!seconds) return "—";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s}s`;
};

const timeAgo = (iso: string) => {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const ChartTip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div
      style={{
        background: "#FFFFFF",
        border: "1px solid #E8E8E8",
        borderRadius: 8,
        padding: "10px 14px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
        fontSize: 12,
      }}
    >
      <p style={{ color: "#999999", marginBottom: 6, fontWeight: 500 }}>
        {label}
      </p>
      {payload.map((p: any) => (
        <div
          key={p.dataKey}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 2,
          }}
        >
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              background: p.fill,
              display: "inline-block",
            }}
          />
          <span style={{ color: "#555555", textTransform: "capitalize" }}>
            {p.name ?? p.dataKey}
          </span>
          <span
            style={{
              color: "#111111",
              fontWeight: 600,
              marginLeft: "auto",
              paddingLeft: 16,
            }}
          >
            {p.value}
          </span>
        </div>
      ))}
    </div>
  );
};

const StatCard = ({
  label,
  value,
  sub,
  trend,
  highlight,
}: {
  label: string;
  value: string | number;
  sub?: string;
  trend?: number;
  highlight?: boolean;
}) => (
  <div className="card">
    <p className="section-label" style={{ marginBottom: 12 }}>
      {label}
    </p>
    <p
      style={{
        fontSize: 36,
        fontWeight: 600,
        letterSpacing: "-0.02em",
        color: highlight ? "#DC2626" : "#111111",
        lineHeight: 1,
        fontVariantNumeric: "tabular-nums",
        margin: 0,
      }}
    >
      {value}
    </p>
    {(sub || trend !== undefined) && (
      <div
        style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 8 }}
      >
        {trend !== undefined && trend !== 0 && (
          <>
            {trend > 0 ? (
              <ArrowUp style={{ width: 11, height: 11, color: "#22C55E" }} />
            ) : (
              <ArrowDown style={{ width: 11, height: 11, color: "#EF4444" }} />
            )}
            <span
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: trend > 0 ? "#22C55E" : "#EF4444",
              }}
            >
              {Math.abs(trend)}%
            </span>
          </>
        )}
        {sub && <span style={{ fontSize: 13, color: "#999999" }}>{sub}</span>}
      </div>
    )}
  </div>
);

const CallerInitial = ({ name }: { name: string }) => {
  const letter = (name || "U")[0].toUpperCase();
  return (
    <div
      style={{
        width: 32,
        height: 32,
        borderRadius: "50%",
        background: "#F7F7F7",
        border: "1px solid #E8E8E8",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        fontSize: 12,
        fontWeight: 600,
        color: "#555555",
      }}
    >
      {letter}
    </div>
  );
};

export const Dashboard = () => {
  const { identity } = useGetIdentity();
  const [conversations, setConversations] = useState<ConversationRow[]>([]);
  const [appointments, setAppointments] = useState<AppointmentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [live, setLive] = useState(false);
  const [newCallId, setNewCallId] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  useEffect(() => {
    const run = async () => {
      const [{ data: convData }, { data: aptData }] = await Promise.all([
        supabase
          .from("conversations")
          .select("*")
          .order("created_at", { ascending: false })
          .limit(100),
        supabase
          .from("appointments")
          .select("*")
          .order("appointment_date", { ascending: true }),
      ]);
      setConversations(convData || []);
      setAppointments(aptData || []);
      setLoading(false);
    };
    run();

    const ch = supabase
      .channel("dashboard-live")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "conversations" },
        (payload) => {
          const row = payload.new as ConversationRow;
          setConversations((prev) => [row, ...prev]);
          setNewCallId(row.id);
          setTimeout(() => setNewCallId(null), 1200);
        },
      )
      .subscribe((status) => {
        setLive(status === "SUBSCRIBED");
      });

    channelRef.current = ch;
    return () => {
      supabase.removeChannel(ch);
    };
  }, []);

  const todayStr = new Date().toISOString().split("T")[0];
  const yesterdayStr = new Date(Date.now() - 86400000)
    .toISOString()
    .split("T")[0];

  const todayCalls = conversations.filter(
    (c) => c.created_at.split("T")[0] === todayStr,
  );
  const yesterdayCalls = conversations.filter(
    (c) => c.created_at.split("T")[0] === yesterdayStr,
  );

  const totalToday = todayCalls.length;
  const totalYesterday = yesterdayCalls.length;
  const bookedToday = todayCalls.filter((c) => c.appointment_booked).length;
  const bookedYesterday = yesterdayCalls.filter(
    (c) => c.appointment_booked,
  ).length;

  const displayCalls = totalToday > 0 ? totalToday : conversations.length;
  const displayBooked =
    totalToday > 0
      ? bookedToday
      : conversations.filter((c) => c.appointment_booked).length;
  const displayLabel = totalToday > 0 ? "today" : "all time";
  const bookingRate = displayCalls
    ? Math.round((displayBooked / displayCalls) * 100)
    : 0;
  const missedCalls = displayCalls - displayBooked;
  const answerRate = displayCalls
    ? Math.round((displayCalls / (displayCalls + 0)) * 100)
    : 100;

  const callTrend = totalYesterday
    ? Math.round(((totalToday - totalYesterday) / totalYesterday) * 100)
    : 0;
  const bookTrend = bookedYesterday
    ? Math.round(((bookedToday - bookedYesterday) / bookedYesterday) * 100)
    : 0;

  const chartData = Array.from({ length: 7 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (6 - i));
    const ds = d.toISOString().split("T")[0];
    const label = d.toLocaleDateString("en-US", { weekday: "short" });
    const day = conversations.filter((c) => c.created_at.split("T")[0] === ds);
    return {
      date: label,
      total: day.length,
      booked: day.filter((c) => c.appointment_booked).length,
    };
  });

  const recentCalls = conversations.slice(0, 10);
  const upcomingApts = appointments
    .filter(
      (a) =>
        a.appointment_date &&
        a.appointment_date >= todayStr &&
        a.status !== "completed",
    )
    .slice(0, 4);

  const thisMonthStr = todayStr.slice(0, 7);
  const monthCalls = conversations.filter((c) =>
    c.created_at.startsWith(thisMonthStr),
  );
  const monthBooked = monthCalls.filter((c) => c.appointment_booked).length;
  const monthRate = monthCalls.length
    ? Math.round((monthBooked / monthCalls.length) * 100)
    : 0;

  if (loading) {
    return (
      <div
        style={{
          minHeight: "calc(100vh - 56px)",
          background: "#F7F7F7",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            width: 20,
            height: 20,
            border: "2px solid #E8E8E8",
            borderTopColor: "#111111",
            borderRadius: "50%",
            animation: "spin 0.8s linear infinite",
          }}
        />
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "calc(100vh - 56px)",
        background: "#F7F7F7",
        padding: 32,
      }}
    >
      {/* Stat cards row */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 20,
          marginBottom: 32,
        }}
      >
        <StatCard
          label={`Calls ${displayLabel}`}
          value={displayCalls}
          trend={totalToday > 0 ? callTrend : undefined}
          sub={
            totalToday > 0
              ? `vs yesterday: ${callTrend >= 0 ? "+" : ""}${callTrend}%`
              : undefined
          }
        />
        <StatCard
          label={`Bookings this ${displayLabel === "today" ? "week" : "period"}`}
          value={displayBooked}
          trend={totalToday > 0 ? bookTrend : undefined}
          sub={`Booking rate: ${bookingRate}%`}
        />
        <StatCard
          label="Missed calls"
          value={missedCalls}
          sub={`Answer rate: ${answerRate}%`}
          highlight={missedCalls > 0}
        />
      </div>

      {/* Two-column layout */}
      <div
        style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: 24 }}
      >
        {/* Left: recent calls + chart */}
        <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
          {/* Recent calls */}
          <div className="card" style={{ padding: 0, overflow: "hidden" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "18px 24px 14px",
              }}
            >
              <span style={{ fontSize: 16, fontWeight: 600, color: "#111111" }}>
                Recent Calls
              </span>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                {live && (
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 5,
                      fontSize: 12,
                      color: "#22C55E",
                    }}
                  >
                    <span
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        background: "#22C55E",
                        display: "inline-block",
                      }}
                    />
                    Live
                  </span>
                )}
                <Link
                  to="/conversations"
                  style={{
                    fontSize: 14,
                    color: "#555555",
                    textDecoration: "none",
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.color = "#111111";
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.color = "#555555";
                  }}
                >
                  View all →
                </Link>
              </div>
            </div>

            {recentCalls.length === 0 ? (
              <div
                style={{
                  padding: "32px 24px",
                  textAlign: "center",
                  color: "#999999",
                  fontSize: 14,
                }}
              >
                No calls yet. Try calling {SERA_PHONE}
              </div>
            ) : (
              recentCalls.map((c, i) => (
                <Link
                  key={c.id}
                  to="/conversations"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 16,
                    padding: "14px 24px",
                    borderBottom:
                      i < recentCalls.length - 1 ? "1px solid #E8E8E8" : "none",
                    textDecoration: "none",
                    background: newCallId === c.id ? "#F0FDF4" : "transparent",
                    transition: "background 0.15s",
                  }}
                  onMouseEnter={(e) => {
                    if (newCallId !== c.id)
                      (e.currentTarget as HTMLElement).style.background =
                        "#F7F7F7";
                  }}
                  onMouseLeave={(e) => {
                    if (newCallId !== c.id)
                      (e.currentTarget as HTMLElement).style.background =
                        "transparent";
                  }}
                >
                  <CallerInitial name={c.caller_name || "U"} />
                  <span
                    style={{
                      fontSize: 14,
                      fontWeight: 500,
                      color: "#111111",
                      flex: 1,
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {c.phone_number || c.caller_name || "Unknown caller"}
                  </span>
                  <span
                    style={{
                      fontSize: 13,
                      color: "#999999",
                      marginLeft: "auto",
                    }}
                  >
                    {fmt(c.duration_seconds)}
                  </span>
                  <span
                    className={`pill ${c.appointment_booked ? "pill-green" : "pill-gray"}`}
                  >
                    {c.appointment_booked ? "Booked" : "Inquiry"}
                  </span>
                  <span style={{ fontSize: 12, color: "#999999" }}>
                    {timeAgo(c.created_at)}
                  </span>
                </Link>
              ))
            )}
          </div>

          {/* Call volume chart */}
          <div className="card">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 20,
              }}
            >
              <span style={{ fontSize: 16, fontWeight: 600, color: "#111111" }}>
                Call volume — last 7 days
              </span>
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                {[
                  { color: "#E8E8E8", label: "Total" },
                  { color: "#111111", label: "Booked" },
                ].map((l) => (
                  <div
                    key={l.label}
                    style={{ display: "flex", alignItems: "center", gap: 5 }}
                  >
                    <span
                      style={{
                        width: 7,
                        height: 7,
                        borderRadius: 2,
                        background: l.color,
                        display: "inline-block",
                      }}
                    />
                    <span
                      style={{
                        fontSize: 11,
                        color: "#999999",
                        fontWeight: 500,
                      }}
                    >
                      {l.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <ResponsiveContainer width="100%" height={160}>
              <BarChart
                data={chartData}
                barSize={14}
                barGap={3}
                barCategoryGap="35%"
              >
                <CartesianGrid
                  vertical={false}
                  stroke="#E8E8E8"
                  strokeDasharray="0"
                />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11, fill: "#999999", fontWeight: 500 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: "#999999" }}
                  axisLine={false}
                  tickLine={false}
                  width={22}
                  allowDecimals={false}
                />
                <Tooltip
                  content={<ChartTip />}
                  cursor={{ fill: "#F7F7F7", radius: 4 }}
                />
                <Bar
                  dataKey="total"
                  name="Total calls"
                  fill="#E8E8E8"
                  radius={[4, 4, 0, 0]}
                />
                <Bar
                  dataKey="booked"
                  name="Booked"
                  fill="#111111"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Right rail */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Demo line card */}
          <div className="card">
            <p className="section-label" style={{ marginBottom: 10 }}>
              Demo Line
            </p>
            <p
              style={{
                fontSize: 22,
                fontWeight: 600,
                color: "#111111",
                letterSpacing: "-0.02em",
                margin: "0 0 4px",
              }}
            >
              {SERA_PHONE}
            </p>
            <p style={{ fontSize: 13, color: "#999999", margin: "0 0 14px" }}>
              Call to test Sera live
            </p>
            <button
              className="btn-secondary"
              style={{ width: "100%" }}
              onClick={() => {
                navigator.clipboard.writeText(SERA_PHONE);
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
              }}
            >
              {copied ? "Copied!" : "Copy number"}
            </button>
          </div>

          {/* This month card */}
          <div className="card">
            <p className="section-label" style={{ marginBottom: 12 }}>
              This Month
            </p>
            {[
              { label: "Total calls", value: monthCalls.length },
              { label: "Total bookings", value: monthBooked },
              { label: "Booking rate", value: `${monthRate}%` },
            ].map((row) => (
              <div
                key={row.label}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "8px 0",
                  borderBottom: "1px solid #F7F7F7",
                }}
              >
                <span style={{ fontSize: 14, color: "#555555" }}>
                  {row.label}
                </span>
                <span
                  style={{ fontSize: 15, fontWeight: 600, color: "#111111" }}
                >
                  {row.value}
                </span>
              </div>
            ))}
          </div>

          {/* Upcoming appointments */}
          {upcomingApts.length > 0 && (
            <div className="card">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 12,
                }}
              >
                <p className="section-label">Upcoming</p>
                <Link
                  to="/appointments"
                  style={{
                    fontSize: 12,
                    color: "#555555",
                    textDecoration: "none",
                  }}
                >
                  View all →
                </Link>
              </div>
              {upcomingApts.map((a) => (
                <div
                  key={a.id}
                  style={{
                    padding: "8px 0",
                    borderBottom: "1px solid #F7F7F7",
                  }}
                >
                  <p
                    style={{
                      fontSize: 14,
                      fontWeight: 500,
                      color: "#111111",
                      margin: "0 0 2px",
                    }}
                  >
                    {a.patient_name || a.caller_name || "Patient"}
                  </p>
                  <p style={{ fontSize: 12, color: "#999999", margin: 0 }}>
                    {a.appointment_date}{" "}
                    {a.appointment_time ? `at ${a.appointment_time}` : ""}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
